import {
  doc,
  onSnapshot,
  getDoc,
  setDoc,
  collection,
  query,
  where,
  getDocs,
  deleteDoc,
  limit,
  orderBy,
  arrayUnion,
  Timestamp,
  deleteField,
  getFirestore,
  DocumentReference,
  Unsubscribe,
  DocumentData,
  FieldValue,
  FirestoreError,
} from "firebase/firestore";
import { db, dbCentral } from "../config/FirebaseConfig.js"; // Import the Firebase setup
import { RefObject } from "react";
import type { OrderByDirection } from "firebase/firestore";

export const convertSecondsToFirestoreTimestamp = (seconds: number) => {
  // Firestore expects milliseconds, so convert seconds to milliseconds
  const milliseconds = seconds * 1000;

  // Create and return a Firestore Timestamp
  return Timestamp.fromMillis(milliseconds);
};

export async function getUserDataById(id: string) {
  if (!id) {
    console.error("User ID is missing");
    return null;
  }
  const docRef = doc(dbCentral, "user", id);
  const docSnapshot = await getDoc(docRef!);
  if (docSnapshot.exists()) {
    return { id: id, ref: docRef, ...docSnapshot.data() }; // Return plain object with document reference and ID
  }
  return null; // Return null if no document exists
}
export async function getOrgDataById(id: string) {
  if (!id) {
    console.error("Organization ID is missing");
    return null;
  }
  const docRef = doc(dbCentral, "org", id);
  const docSnapshot = await getDoc(docRef);
  if (docSnapshot.exists()) {
    return { id: id, ref: docRef, ...docSnapshot.data() }; // Return plain object with document reference and ID
  }
  return null; // Return null if no document exists
}
export async function getWorkspaceDataById(id: string) {
  if (!id) {
    console.error("Workspace ID is missing");
    return null;
  }
  const docRef = doc(dbCentral, "workspace", id);
  const docSnapshot = await getDoc(docRef);
  if (docSnapshot.exists()) {
    return { id: id, ref: docRef, ...docSnapshot.data() }; // Return plain object with document reference and ID
  }
  return null; // Return null if no document exists
}

export async function getDocDataById(
  collection: string,
  id: string | undefined
) {
  if (!collection || !id) {
    console.error("Collection or ID is missing");
    return null;
  }
  const docRef = doc(db, collection, id);
  const docSnapshot = await getDoc(checkDB(docRef)!);
  if (docSnapshot.exists()) {
    return { id: id, ref: docRef, ...docSnapshot.data() }; // Return plain object with document reference and ID
  }
  return null; // Return null if no document exists
}

export function checkDB(docRef: DocumentReference<any, any> | null) {
  if (!docRef) {
    console.error("Document reference is missing");
    return null;
  }
  if (!(docRef instanceof DocumentReference)) {
    console.error("Invalid document reference provided");
    return null;
  }
  // If the document reference is from the central database, rebuild it
  if (
    docRef.path.startsWith("user/") ||
    docRef.path.startsWith("org/") ||
    docRef.path.startsWith("workspace/")
  ) {
    // Rebuild the document reference to point to the central database
    return doc(dbCentral, docRef.path);
  }
  return docRef; // Return the original reference if it's not from the central database
}

export async function getDocDataByRef(
  docRef: DocumentReference<any, any> | null
) {
  if (!docRef) {
    console.error("Document reference is missing");
    return null;
  }
  if (!(docRef instanceof DocumentReference)) {
    console.error("Invalid document reference provided");
    return null;
  }
  // if collection is user, org or workspace, raise an error
  docRef = checkDB(docRef);
  const docSnapshot = await getDoc(docRef!);
  if (docSnapshot.exists()) {
    return { id: docSnapshot.id, ref: docRef, ...docSnapshot.data() }; // Return plain object with document reference and ID
  }
  return null; // Return null if no document exists
}

export function listenDocDataByRef(
  docRef: DocumentReference<any, any> | null,
  callback: (arg0: any) => void
) {
  docRef = checkDB(docRef);

  if (!docRef) {
    console.error("Document reference is missing or invalid for onSnapshot.");
    return () => {};
  }

  const unsubscribe = onSnapshot(
    docRef,
    (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = { id: docSnapshot.id, ref: docRef, ...docSnapshot.data() };
        callback(data);
      } else {
      }
    },
    (error) => {
      console.error("Error fetching folder:", error);
    }
  );

  return unsubscribe;
}

export function listenDocDataByRefX(
  docRef: DocumentReference<any, any> | null,
  callback: (arg0: any) => void,
  unsubscribeRef: { current: (() => void) | null }
) {

  // Ensure we unsubscribe from the previous listener before creating a new one
  if (unsubscribeRef.current) {
    unsubscribeRef.current(); // Stop the old listener
  }

  // Set up a new listener
  if (!docRef) {
    console.error("Document reference is missing or invalid for onSnapshot.");
    return () => {};
  }
  unsubscribeRef.current = onSnapshot(
    docRef,
    (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = { id: docSnapshot.id, ref: docRef, ...docSnapshot.data() };
        callback(data);
      }
    },
    (error) => {
      console.error("Error fetching document:", error);
    }
  );

  // Return a cleanup function to stop listening when needed
  return () => {
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
    }
  };
}

export function listenDocDataById(
  collection: string,
  id: string,
  callback: (arg0: any) => void
) {
  let docRef = doc(db, collection, id);
  docRef = checkDB(docRef)!;

  return listenDocDataByRef(docRef, callback);
}

export function listenDocDataByIdX(
  collection: string,
  id: string,
  callback: { (data: any): void; (data: any): void; (arg0: any): void },
  unsubscribeRef: RefObject<(() => void) | null>
) {
  let docRef = doc(db, collection, id);

  return listenDocDataByRefX(docRef, callback, unsubscribeRef);
}

export async function createDoc(
  collectionName: string,
  data: {
    name: any;
    description?: any;
    custom?: boolean;
    docXX_ref?: any;
    icon?: any;
    infos?: any;
    storage_path?: null;
    state?: string;
    documentxx?: any;
  }
) {
  // Create a reference to the collection
  const docRef = doc(collection(db, collectionName)); // Automatically generates a document ID
  // Write the document
  await setDoc(docRef, data);
  // Fetch the newly created document
  const docSnapshot = await getDoc(docRef);
  return { id: docSnapshot.id, ref: docRef, ...docSnapshot.data() }; // Return the document's data with its ID and reference
}

export async function deleteDocById(collection: string, id: string) {
  const docRef = doc(db, collection, id);
  await deleteDoc(docRef);
}

export function last_of_list(list: string | any[]) {
  return list[list.length - 1];
}

export async function deleteDocByRef(
  docRef: DocumentReference<unknown, DocumentData>
) {
  await deleteDoc(docRef);
}

export async function getArrayDocRefByIDs(collection: any, ids: any[]) {
  if (!ids) {
    console.error("List of document IDs is missing");
    return [];
  }
  const docRefs = ids.map((id) => checkDB(getRefFromId(collection, id)));
  return docRefs;
}

export async function generateDocumentsXFromDocumentsXX(documentsXXRefs: any) {
  if (!documentsXXRefs) {
    console.error("List of documentXX references is missing");
    return [];
  }
  const documentsX = [];
  for (let docXXRef of documentsXXRefs) {
    docXXRef = checkDB(docXXRef); // Ensure the reference is valid
    const docXXData = await getDocDataByRef(docXXRef);
    if (docXXData) {
      const docXData = {
        name: docXXData.name,
        description: docXXData.description,
        custom: false,
        docXX_ref: docXXRef,
      };
      const docX = await createDoc("documentx", docXData);
      documentsX.push(docX);
    }
  }
  return documentsX;
}

export async function generateDocumentsXRefsFromDocumentsXX(
  documentsXXRefs: any
) {
  if (!documentsXXRefs) {
    console.error("List of documentXX references is missing");
    return [];
  }
  const documentsXRefs = [];
  for (let docXXRef of documentsXXRefs) {
    docXXRef = checkDB(docXXRef); // Ensure the reference is valid
    const docXXData = await getDocDataByRef(docXXRef);
    if (docXXData) {
      const docXData = {
        name: docXXData.name,
        icon: docXXData.icon,
        infos: docXXData.infos,
        storage_path: null,
        state: "locked",
        documentxx: docXXRef,
      };
      const docX = await createDoc("documentx", docXData);
      documentsXRefs.push(docX.ref);
    }
  }
  return documentsXRefs;
}

export async function getArrayDocDataByRef(
  listdocRef: any[] | null | undefined
) {
  if (listdocRef === null || listdocRef === undefined) {
    console.error("List of document references is missing");
    return [];
  }
  if (listdocRef?.length === 0) {
    return [];
  }

  try {
    // Use Promise.all to fetch all documents concurrently
    const docSnapshots = await Promise.all(
      listdocRef
        .map((docRef) => checkDB(docRef))
        .filter((ref): ref is DocumentReference<any, any> => ref !== null)
        .map((docRef) => getDoc(docRef))
    );
    // Filter and map the results to include only existing documents
    const docs = docSnapshots
      .filter((docSnapshot) => docSnapshot.exists())
      .map((docSnapshot) => ({
        id: docSnapshot.id,
        ref: docSnapshot.ref,
        ...docSnapshot.data(),
      }));

    return docs;
  } catch (error) {
    console.error("Error fetching documents:", error);
    return [];
  }
}

export async function getArrayDocDataByID(
  collection: string,
  listdocID: string | any[]
) {
  if (!listdocID) {
    console.error("List of document references is missing");
    return [];
  }
  if (listdocID?.length === 0) {
    return [];
  }
  let listdocRef = [];
  for (const docID of listdocID) {
    const docRef = doc(db, collection, docID);
    listdocRef.push(checkDB(docRef));
  }

  try {
    // Use Promise.all to fetch all documents concurrently
    const docSnapshots = await Promise.all(
      listdocRef.map((docRef) => getDoc(docRef!))
    );
    // Filter and map the results to include only existing documents
    const docs = docSnapshots
      .filter((docSnapshot) => docSnapshot.exists())
      .map((docSnapshot) => ({
        id: docSnapshot.id,
        ref: docSnapshot.ref,
        ...docSnapshot.data(),
      }));

    return docs;
  } catch (error) {
    console.error("Error fetching documents:", error);
    return [];
  }
}

export function getFlutterTime() {
  return Timestamp.now();
}

export function getLocalDateFromFlutter(
  flutter_date: Timestamp | undefined
): string | undefined {
  if (!flutter_date) {
    return undefined;
  }
  // Convert Flutter timestamp to JavaScript Date
  return new Date(flutter_date?.seconds * 1000).toLocaleDateString("fr-FR", {
    timeZone: "Europe/Paris", // Adjust to your desired timezone
  });
}
export function getFlutterTimeFromDateTime(
  day: number,
  month: number,
  year: number,
  hour: number = 0,
  minute: number = 0,
  second: number = 0
) {
  // Create a Date object from the provided date and time components
  const date = new Date(year, month - 1, day, hour, minute, second);
  // Convert the Date object to a Firestore Timestamp
  return Timestamp.fromDate(date);
}

export function getLocalDateFromFlutterString(
  flutter_date:
    | {
        seconds: number;
      }
    | undefined
): string {
  if (!flutter_date) {
    return "";
  }
  // Convert Flutter timestamp to JavaScript Date
  return new Date(flutter_date.seconds * 1000).toLocaleDateString("fr-FR", {
    timeZone: "Europe/Paris", // Adjust to your desired timezone
  });
}

export function getLocalFromFlutterString(flutter_date: {
  seconds: number;
}): string {
  if (!flutter_date) {
    return "";
  }
  // Convert Flutter timestamp to JavaScript Date
  return new Date(flutter_date.seconds * 1000).toLocaleString("fr-FR", {
    timeZone: "Europe/Paris", // Adjust to your desired timezone
  });
}

export function getFlutterTimeFromSeconds(seconds: number) {
  return Timestamp.fromMillis(seconds * 1000);
}

export function getDateFromFlutter(flutter_date: { seconds: number }) {
  if (!flutter_date) {
    return null;
  }
  return new Date(flutter_date?.seconds * 1000);
}

export function getDateFromFirebase(start: { seconds: number }) {
  return new Date(start.seconds * 1000);
}

export function getDateFromSeconds(seconds: number) {
  return new Date(seconds * 1000);
}
export function getFlutterTimeFromSecondsFormatDate(seconds: number) {
  // to ("DD/MM/YYYY")
  return getFlutterTimeFromSeconds(seconds)
    .toDate()
    .toLocaleDateString("fr-FR");
}

export function getFlutterTimeFromSecondsFormatTime(seconds: number) {
  // to ("HH:mm")
  return getFlutterTimeFromSeconds(seconds)
    .toDate()
    .toLocaleTimeString("fr-FR");
  // to ("DD/MM/YYYY")
}

export async function updateDocDataField(
  docRef: DocumentReference<any, any> | null,
  field: any,
  value: any
) {
  const checkedRef = checkDB(docRef);
  if (!checkedRef) {
    console.error("Document reference is missing or invalid.");
    return;
  }
  await setDoc(checkedRef, { [field]: value }, { merge: true });
}

export async function deleteDocDataField(
  docRef: DocumentReference<any, any> | null,
  field: any
) {
  const checkedRef = checkDB(docRef);
  if (!checkedRef) {
    console.error("Document reference is missing or invalid.");
    return;
  }
  await setDoc(checkedRef, { [field]: deleteField() }, { merge: true });
}

export function getRefFromId(collection: string, id: string) {
  return checkDB(doc(db, collection, id));
}

export async function getDocumentsByFolderRef(
  folderRef: DocumentReference<unknown, DocumentData>
) {
  const docSnapshot = await getDoc(folderRef);
  const docData = docSnapshot.data();
  const docs = [];

  if (docData && Array.isArray((docData as any).documents)) {
    for (const docRef of (docData as any).documents) {
      const docSnapshot = await getDoc(docRef);
      if (docSnapshot.exists()) {
        const data = docSnapshot.data() || {};
        docs.push({
          id: docRef.id,
          ref: docRef,
          ...data,
        }); // Include document reference and ID
      }
    }
  }
  return docs || [];
}

export function TruncatedString(text: string, maxLength = 20) {
  const truncatedText =
    text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
  return truncatedText;
}

// Dynamic query function

export async function queryCollection(
  collectionName: string,
  filters: any[],
  limite = 10000,
  sortingField: string | null = null,
  sortingOrder: OrderByDirection = "asc"
) {
  if (!collectionName) {
    console.error("Collection name is missing");
    return [];
  }
  let objectsQuery;
  // Initialize query with the collection reference
  if (
    collectionName === "user" ||
    collectionName === "org" ||
    collectionName === "workspace"
  ) {
    objectsQuery = collection(dbCentral, collectionName);
  } else {
    objectsQuery = collection(db, collectionName);
  }

  // Apply dynamic filters if provided
  if (filters && filters.length > 0) {
    const queries = filters.map((filter) =>
      where(filter.field, filter.operator, filter.value)
    );
    objectsQuery = query(objectsQuery, ...queries);
  }

  // Apply sorting if a sortingField is provided
  if (sortingField) {
    objectsQuery = query(objectsQuery, orderBy(sortingField, sortingOrder));
  }

  // Apply the limit to the query
  objectsQuery = query(objectsQuery, limit(limite));

  try {
    // Fetch the documents that match the query
    const querySnapshot = await getDocs(objectsQuery);

    // Map the documents to an array of data with document ID and reference
    const objects = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ref: doc.ref,
      ...doc.data(),
    }));

    return objects || [];
  } catch (error) {
    console.error("Error querying collection:", error);
    return [];
  }
}
export function listenCollection(
  collectionName: string,
  filters: any[],
  limite = 1000,
  sortingField: string | null = null,
  sortingOrder: OrderByDirection = "asc",
  onDataChange: (
    arg0: { id: string; ref: DocumentReference<DocumentData, DocumentData> }[]
  ) => void,
  unsubscribeRef: { current: (() => void) | null } | null = null,
  onError: (arg0: FirestoreError) => void
) {
  if (!collectionName) {
    console.error("Collection name is missing");
    return null;
  }

  if (unsubscribeRef?.current) {
    unsubscribeRef.current();
  }

  let objectsQuery;
  if (
    collectionName === "user" ||
    collectionName === "org" ||
    collectionName === "workspace"
  ) {
    // Initialize query with the central database reference
    objectsQuery = collection(dbCentral, collectionName);
  } else {
    // Initialize query with the regular database reference
    objectsQuery =
      // Initialize query with the collection reference
      collection(db, collectionName);
  }

  // Apply dynamic filters if provided
  if (filters && filters.length > 0) {
    const queries = filters.map((filter) =>
      where(filter.field, filter.operator, filter.value)
    );
    objectsQuery = query(objectsQuery, ...queries);
  }

  // Apply sorting if a sortingField is provided
  if (sortingField) {
    objectsQuery = query(objectsQuery, orderBy(sortingField, sortingOrder));
  }

  // Apply the limit to the query
  objectsQuery = query(objectsQuery, limit(limite));

  try {
    // Set up a listener for the query

    let current = onSnapshot(
      objectsQuery,
      (querySnapshot) => {
        const objects = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ref: doc.ref,
          ...doc.data(),
        }));

        // Call the onDataChange callback with the updated data
        if (onDataChange) {
          onDataChange(objects);
        }
      },
      (error) => {
        console.error("Error listening to collection:", error);
        if (onError) {
          onError(error);
        }
      }
    );

    if (unsubscribeRef) {
      unsubscribeRef.current = current;
    }

    return () => {
      if (unsubscribeRef?.current) {
        unsubscribeRef.current();
      }
    };
  } catch (error) {
    console.error("Error setting up snapshot listener:", error);
    return null;
  }
}
